import { useCallback, useEffect, useMemo, useRef } from "react";
import { digitsToLatin } from "./numbers";
function diff_commonPrefix(text1, text2) {
    // Quick check for common null cases.
    if (!text1 || !text2 || text1.charCodeAt(0) !== text2.charCodeAt(0)) {
        return 0;
    }
    // Binary search.
    let pointermin = 0;
    let pointermax = Math.min(text1.length, text2.length);
    let pointermid = pointermax;
    let pointerstart = 0;
    while (pointermin < pointermid) {
        if (text1.substring(pointerstart, pointermid) ==
            text2.substring(pointerstart, pointermid)) {
            pointermin = pointermid;
            pointerstart = pointermin;
        }
        else {
            pointermax = pointermid;
        }
        pointermid = Math.floor((pointermax - pointermin) / 2 + pointermin);
    }
    return pointermid;
}
function diff_commonSuffix(text1, text2) {
    // Quick check for common null cases.
    if (!text1 ||
        !text2 ||
        text1.charCodeAt(text1.length - 1) !== text2.charCodeAt(text2.length - 1)) {
        return 0;
    }
    // Binary search.
    let pointermin = 0;
    let pointermax = Math.min(text1.length, text2.length);
    let pointermid = pointermax;
    let pointerend = 0;
    while (pointermin < pointermid) {
        if (text1.substring(text1.length - pointermid, text1.length - pointerend) ==
            text2.substring(text2.length - pointermid, text2.length - pointerend)) {
            pointermin = pointermid;
            pointerend = pointermin;
        }
        else {
            pointermax = pointermid;
        }
        pointermid = Math.floor((pointermax - pointermin) / 2 + pointermin);
    }
    return pointermid;
}
// getCleanPosition because position of cursor is in formatted value we should ignore none integer
// example "7777 7#7" value of selectionRef.current.start is 6 but the real position
// is 5 ==> "77777#7"
function getCleanPosition(position, formattedValue) {
    const val = digitsToLatin(formattedValue);
    for (let i = 0; i < position; i++) {
        if (!val[i].match(/[0-9]/)) {
            position--;
        }
    }
    return position;
}
export const useCursorPosition = (value, inputField, formatter) => {
    const selectionRef = useRef({
        start: null,
        value: "",
    });
    const previousValueRef = useRef(value);
    const findCursorPosition = useCallback((previous, current) => {
        const isInserted = current.length > previous.length;
        const prefix = diff_commonPrefix(previous, current);
        const suffix = diff_commonSuffix(previous, current);
        if (isInserted) {
            // if suffix === 0 it means insertation at the end Suffix(123, 1234) = 0 characters
            if (suffix === 0) {
                return current.length;
            }
            // if prefix === 0 it means insertation at the start prefix(123, 4123) = 0 characters
            if (prefix === 0) {
                return current.length - suffix;
            }
            // if after insert, sum(prefix + suffix) equal to (newValue) it means repeated characters
            // example prefix(123, 1233) = 3 characters suffix(123, 1233) = 1 characters ==> 3 + 1 == "1233".length
            // cursor position is = lastPosition + (number of characters added)
            // >= because of example suffix(12333, 123333)
            if (prefix + suffix >= current.length) {
                const numberOfCharactersAdded = current.length - previous.length;
                if (selectionRef.current.start) {
                    const { start, value } = selectionRef.current;
                    const previousPosition = getCleanPosition(start, value);
                    return previousPosition + numberOfCharactersAdded;
                }
                return previous.length + numberOfCharactersAdded;
            }
            // last situation is insert between text Suffix(123, 1243) = 1
            return current.length - suffix;
        }
        else {
            // if suffix === 0 it means delete at the end Suffix(1234, 123) = 0 characters
            if (suffix === 0) {
                return current.length;
            }
            // if prefix === 0 it means delete at the start prefix(4123, 123) = 0 characters
            if (prefix === 0) {
                return current.length - suffix;
            }
            // if after delete, sum(prefix + suffix) equal to (oldValue) it means repeated characters delete
            // example prefix(1233, 123) = 3 characters suffix(1233, 123) = 1 characters ==> 3 + 1 == "1233".length
            // cursor position is = lastPosition - (number of characters deleted)
            // >= because of example Suffix(123333, 12333)
            if (prefix + suffix >= previous.length) {
                const numberOfCharactersDeleted = previous.length - current.length;
                if (selectionRef.current.start) {
                    const { start, value } = selectionRef.current;
                    const previousPosition = getCleanPosition(start, value);
                    return previousPosition - numberOfCharactersDeleted;
                }
                return previous.length - numberOfCharactersDeleted;
            }
            // last situation is delete between text Prefix(1243, 123) = 2
            return prefix;
        }
    }, []);
    useEffect(() => {
        const handler = () => {
            if (inputField.current && inputField.current === document.activeElement) {
                selectionRef.current = {
                    start: inputField.current.selectionStart,
                    value: inputField.current.value,
                };
            }
        };
        document.addEventListener("selectionchange", handler);
        return () => {
            document.removeEventListener("selectionchange", handler);
        };
    }, []);
    return useMemo(() => {
        const rawPosition = findCursorPosition(previousValueRef.current, value);
        previousValueRef.current = value;
        if (!formatter) {
            return rawPosition;
        }
        // find correct position in formatted value
        // example: Cursor(12345#6) is 5 but correctPosition is 6 after format
        // rawValue = "123456" formatted "1234 56"
        const formattedValue = formatter(value);
        let j = 0;
        for (let i = 0; i < rawPosition && j < formattedValue.length;) {
            if (formattedValue[j].match(/[0-9۰-۹]/)) {
                i++;
            }
            j++;
        }
        return j;
    }, [value]);
};
//# sourceMappingURL=hooks.js.map